import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Plus, Trash2, Settings, ChevronRight, Building2, X, Copy, Upload } from 'lucide-react';
import axios from 'axios';
import useConfirmDialog from '../hooks/useConfirmDialog';
import './EmployerClients.scss';

interface EmployerClient {
  id: number;
  employer_id: number;
  name: string;
  activity_sector?: string;
  logo?: string | null;
  descriptions?: string[];
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const UPLOADS_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const getClientLogoUrl = (filename?: string | null): string | null =>
  filename ? `${UPLOADS_BASE}/uploads/clients/${filename}` : null;

const EmployerClients: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const [clients, setClients] = useState<EmployerClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<EmployerClient | null>(null);
  const [modalForm, setModalForm] = useState({
    name: '',
    descriptions: [''] as string[]
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [saving, setSaving] = useState(false);

  // Gear menu
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const loadClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${API_URL}/employer-clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setClients(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Close gear menu on outside click
  useEffect(() => {
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  if (isLoading) return <div className="employer-clients-page"><div className="loading">Kraunama...</div></div>;
  if (!isAuthenticated || user?.user_type !== 'employer') return <Navigate to="/" replace />;
  if (!user?.is_recruitment_agency) return <Navigate to="/" replace />;

  const resetLogoState = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(false);
  };

  const openNewModal = () => {
    setEditingClient(null);
    setModalForm({ name: '', descriptions: [''] });
    resetLogoState();
    setShowModal(true);
  };

  const openEditModal = (client: EmployerClient) => {
    setEditingClient(client);
    setModalForm({
      name: client.name,
      descriptions: client.descriptions && client.descriptions.length > 0 ? [...client.descriptions] : ['']
    });
    setLogoFile(null);
    setLogoPreview(getClientLogoUrl(client.logo));
    setRemoveLogo(false);
    setShowModal(true);
    setOpenMenuId(null);
  };

  const handleDuplicate = (client: EmployerClient) => {
    setEditingClient(null);
    setModalForm({
      name: `${client.name} (kopija)`,
      descriptions: client.descriptions && client.descriptions.length > 0 ? [...client.descriptions] : ['']
    });
    resetLogoState();
    setShowModal(true);
    setOpenMenuId(null);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) return;
    setLogoFile(file);
    setRemoveLogo(false);
    const reader = new FileReader();
    reader.onload = ev => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(true);
  };

  const handleDelete = async (id: number) => {
    if (!await confirm('Ar tikrai norite ištrinti šį klientą?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API_URL}/employer-clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(prev => prev.filter(c => c.id !== id));
    } catch {
      alert('Klaida trinant klientą');
    }
  };

  const handleSave = async () => {
    if (!modalForm.name.trim()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const fd = new FormData();
      fd.append('name', modalForm.name.trim());
      fd.append('descriptions', JSON.stringify(modalForm.descriptions.filter(d => d.trim())));
      if (logoFile) fd.append('logo', logoFile);
      if (removeLogo) fd.append('remove_logo', 'true');

      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (editingClient) {
        await axios.put(`${API_URL}/employer-clients/${editingClient.id}`, fd, config);
      } else {
        await axios.post(`${API_URL}/employer-clients`, fd, config);
      }

      setShowModal(false);
      loadClients();
    } catch {
      alert('Klaida išsaugant klientą');
    } finally {
      setSaving(false);
    }
  };

  const addDescription = () => {
    setModalForm(prev => ({ ...prev, descriptions: [...prev.descriptions, ''] }));
  };

  const updateDescription = (index: number, value: string) => {
    setModalForm(prev => ({
      ...prev,
      descriptions: prev.descriptions.map((d, i) => i === index ? value : d)
    }));
  };

  const removeDescription = (index: number) => {
    if (modalForm.descriptions.length <= 1) return;
    setModalForm(prev => ({
      ...prev,
      descriptions: prev.descriptions.filter((_, i) => i !== index)
    }));
  };

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchFilter.toLowerCase())
  );

  return (
    <div className="employer-clients-page">
      {ConfirmDialogNode}
      <div className="container">
        <div className="breadcrumbs">
          <Link to="/">Titulinis</Link>
          <ChevronRight size={14} />
          <span>Klientai</span>
        </div>

        <div className="header-row">
          <div className="tab-title">
            Klientai
            {clients.length > 0 && <span className="count">{clients.length}</span>}
          </div>
          <div className="filters-toolbar">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Kliento pavadinimas"
                value={searchFilter}
                onChange={e => setSearchFilter(e.target.value)}
                className="search-title-input"
              />
            </div>
            <button className="btn-new-client" onClick={openNewModal}>
              <Plus size={16} />
              <span>Naujas klientas</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Kraunami klientai...</div>
        ) : filteredClients.length === 0 && clients.length === 0 ? (
          <div className="empty-state">
            <Building2 size={48} />
            <h3>Jūs dar neturite klientų</h3>
            <p>Pridėkite pirmąjį klientą, kad galėtumėte jį priskirti darbo skelbimams.</p>
            <button className="btn btn--primary" onClick={openNewModal}>
              <Plus size={20} /> Pridėti pirmąjį klientą
            </button>
          </div>
        ) : (
          <div className="clients-grid">
            {filteredClients.map(client => (
              <div key={client.id} className="client-card">
                <div className="client-card__actions">
                  <div className="gear-menu-wrapper">
                    <button
                      className="action-icon-btn"
                      onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === client.id ? null : client.id); }}
                    >
                      <Settings size={16} />
                    </button>
                    {openMenuId === client.id && (
                      <div className="gear-dropdown" onClick={e => e.stopPropagation()}>
                        <button onClick={() => openEditModal(client)}>
                          <Settings size={14} /> Redaguoti
                        </button>
                        <button onClick={() => handleDuplicate(client)}>
                          <Copy size={14} /> Kopijuoti
                        </button>
                      </div>
                    )}
                  </div>
                  <button className="action-icon-btn delete-btn" onClick={() => handleDelete(client.id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="client-card__logo">
                  {getClientLogoUrl(client.logo) ? (
                    <img src={getClientLogoUrl(client.logo)!} alt={client.name} />
                  ) : (
                    <div className="client-card__logo-placeholder">
                      <Building2 size={28} />
                    </div>
                  )}
                </div>
                <h3 className="client-card__name">{client.name}</h3>
                <p className="client-card__desc">
                  {client.descriptions && client.descriptions.length > 0
                    ? client.descriptions[0].substring(0, 200) + (client.descriptions[0].length > 200 ? '...' : '')
                    : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="client-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="client-modal" onClick={e => e.stopPropagation()}>
            <div className="client-modal__header">
              <h2>
                <Building2 size={20} />
                {editingClient ? editingClient.name : 'Naujas klientas'}
              </h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="client-modal__body">
              <div className="form-group">
                <label><span className="req">*</span> Įmonės pavadinimas</label>
                <input
                  type="text"
                  value={modalForm.name}
                  onChange={e => setModalForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Įmonės pavadinimas"
                />
              </div>

              <div className="form-group">
                <label>Įmonės logotipas</label>
                <div className="client-logo-upload">
                  <div className="client-logo-preview">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Kliento logotipas" />
                    ) : (
                      <div className="client-logo-placeholder">
                        <Building2 size={32} />
                      </div>
                    )}
                  </div>
                  <div className="client-logo-actions">
                    <input
                      type="file"
                      id="client_logo"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="client-logo-input"
                    />
                    <label htmlFor="client_logo" className="client-logo-btn">
                      <Upload size={16} />
                      {logoPreview ? 'Keisti logotipą' : 'Įkelti logotipą'}
                    </label>
                    {logoPreview && (
                      <button type="button" className="client-logo-remove" onClick={handleRemoveLogo}>
                        <Trash2 size={14} /> Pašalinti
                      </button>
                    )}
                    <small>Maks. 5MB. JPG, PNG, GIF</small>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Įmonės veiklos aprašymai</label>
                {modalForm.descriptions.map((desc, i) => (
                  <div key={i} className="description-row">
                    <textarea
                      value={desc}
                      onChange={e => updateDescription(i, e.target.value)}
                      placeholder="Įmonės veiklos aprašymas..."
                      rows={4}
                    />
                    {modalForm.descriptions.length > 1 && (
                      <button className="remove-desc-btn" onClick={() => removeDescription(i)}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button className="add-desc-btn" onClick={addDescription}>
                  + Pridėti naują įmonės veiklos aprašymą
                </button>
              </div>
            </div>
            <div className="client-modal__footer">
              <button className="save-btn" onClick={handleSave} disabled={saving || !modalForm.name.trim()}>
                {saving ? 'Saugoma...' : '✓ IŠSAUGOTI'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerClients;
